/**
 * ServerPlayerStatsSection
 *
 * Renders server-sourced per-player rollups + team totals for a match.
 * Consumed in the authenticated match analytics area (MatchDetail).
 *
 * Four required UI states:
 *   loading        — spinner while data is in flight
 *   empty          — response has no players / events
 *   error + retry  — fetch failed
 *   permission-denied — caller is a guest; show forbidden, do not crash
 */
import { useState, useEffect, useCallback, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../store/authStore";
import { fetchMatchPlayerStats } from "../../lib/playerStats";
import type {
  PlayerStatsResponse,
  PlayerStats,
  TeamTotals,
} from "../../types/playerStats";
import { exportPlayerStatsCSV, exportPlayerStatsPDF } from "../../lib/export";
import { formatDisplayName } from "../../lib/nameFormat";

type SortableNumericField =
  | "jersey_number"
  | "minutes_played"
  | "events_total"
  | "passes.attempted"
  | "passes.completed"
  | "passes.completion_pct"
  | "shots.total"
  | "shots.on_target"
  | "shots.goals"
  | "cards.yellow"
  | "cards.red"
  | "xg"
  | "progressive_passes"
  | "progressive_carries"
  | "rating";

/** EPIC-07: null (not computable) sorts as lowest via this sentinel — real
 * ratings are clamped to [0, 10], so -1 never collides with a real value. */
const NOT_COMPUTABLE_SORT_VALUE = -1;

/** Position-group values are the backend's own PositionGroup enum
 * (goalkeeper | defense | midfield | attack | other) — the label falls
 * back to the raw string for a value not yet in this list. */
const POSITION_GROUP_LABEL_KEYS: Record<string, string> = {
  goalkeeper: "playerStats.positionGroupGoalkeeper",
  defense: "playerStats.positionGroupDefense",
  midfield: "playerStats.positionGroupMidfield",
  attack: "playerStats.positionGroupAttack",
  other: "playerStats.positionGroupOther",
};

/** Judge round 1, P4: title-casing the raw backend key left the breakdown
 * English-only under a Spanish UI ("Pass Completed" instead of "Pase
 * Completado"). Real i18n lookup keyed by the literal backend key
 * (`playerStats.contributionKeys.<key>`, both `en`/`es` `logger.json`) —
 * `prettifyContributionKey` now serves only as the visible fallback for a
 * key not yet in the locale files (kept in sync with
 * `app/services/player_rating.py`'s `RATING_WEIGHTS`, 25 keys as of the
 * judge round 1 weight-table expansion). */
function prettifyContributionKey(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function contributionKeyLabel(
  t: (key: string, fallback: string) => string,
  key: string,
): string {
  return t(`playerStats.contributionKeys.${key}`, prettifyContributionKey(key));
}

function getPlayerValue(p: PlayerStats, field: SortableNumericField): number {
  switch (field) {
    case "jersey_number":
      return p.jersey_number ?? 0;
    case "minutes_played":
      return p.minutes_played;
    case "events_total":
      return p.events_total;
    case "passes.attempted":
      return p.passes.attempted;
    case "passes.completed":
      return p.passes.completed;
    case "passes.completion_pct":
      return p.passes.completion_pct;
    case "shots.total":
      return p.shots.total;
    case "shots.on_target":
      return p.shots.on_target;
    case "shots.goals":
      return p.shots.goals;
    case "cards.yellow":
      return p.cards.yellow;
    case "cards.red":
      return p.cards.red;
    case "xg":
      return p.xg ?? 0;
    case "progressive_passes":
      return p.progressive_passes ?? 0;
    case "progressive_carries":
      return p.progressive_carries ?? 0;
    case "rating":
      return p.rating ?? NOT_COMPUTABLE_SORT_VALUE;
    default:
      return 0;
  }
}

interface Column {
  key: SortableNumericField;
  abbr: string;
  label: string;
}

const COLUMNS: Column[] = [
  { key: "minutes_played", abbr: "MIN", label: "Minutes" },
  { key: "events_total", abbr: "EVT", label: "Events" },
  { key: "passes.attempted", abbr: "PA", label: "Passes Att." },
  { key: "passes.completed", abbr: "PC", label: "Passes Comp." },
  { key: "passes.completion_pct", abbr: "P%", label: "Pass %" },
  { key: "shots.total", abbr: "SH", label: "Shots" },
  { key: "shots.on_target", abbr: "SOT", label: "On Target" },
  { key: "shots.goals", abbr: "G", label: "Goals" },
  { key: "cards.yellow", abbr: "YC", label: "Yellow" },
  { key: "cards.red", abbr: "RC", label: "Red" },
  { key: "xg", abbr: "xG", label: "Expected Goals (xG)" },
  { key: "progressive_passes", abbr: "PP", label: "Progressive Passes" },
  { key: "progressive_carries", abbr: "PgC", label: "Progressive Carries" },
  { key: "rating", abbr: "RTG", label: "Match Rating" },
];

interface Props {
  matchId: string;
}

export default function ServerPlayerStatsSection({ matchId }: Props) {
  const { t } = useTranslation("logger");
  const user = useAuthStore((s) => s.user);

  const [data, setData] = useState<PlayerStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] =
    useState<SortableNumericField>("shots.goals");
  const [sortAsc, setSortAsc] = useState(false);
  const [teamFilter, setTeamFilter] = useState<string>("all");
  const [exporting, setExporting] = useState<"csv" | "pdf" | null>(null);
  // EPIC-07: which player's rating breakdown row is expanded, if any.
  const [expandedRatingPlayerId, setExpandedRatingPlayerId] = useState<
    string | null
  >(null);

  const isGuest = user?.role === "guest";

  // All hooks must run unconditionally before any conditional return.
  const load = useCallback(async () => {
    if (isGuest) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchMatchPlayerStats(matchId);
      setData(result);
    } catch {
      setError(
        t("playerStats.fetchError", "Failed to load player statistics."),
      );
    } finally {
      setLoading(false);
    }
  }, [matchId, t, isGuest]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setExpandedRatingPlayerId(null);
  }, [matchId]);

  // ── Permission-denied state ──────────────────────────────────────────────
  if (isGuest) {
    return (
      <div
        className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center"
        data-testid="server-player-stats-forbidden"
      >
        <p className="text-amber-700 font-medium">
          {t(
            "playerStats.permissionDenied",
            "You do not have permission to view player statistics.",
          )}
        </p>
      </div>
    );
  }

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-12"
        data-testid="server-player-stats-loading"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        <span className="ml-3 text-gray-500">
          {t("playerStats.loading", "Loading player stats…")}
        </span>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div
        className="rounded-xl border border-red-200 bg-red-50 p-6 text-center space-y-3"
        data-testid="server-player-stats-error"
      >
        <p className="text-red-700">{error}</p>
        <button
          onClick={load}
          className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
          data-testid="server-player-stats-retry"
        >
          {t("playerStats.retry", "Retry")}
        </button>
      </div>
    );
  }

  // ── Empty ────────────────────────────────────────────────────────────────
  if (!data || data.players.length === 0) {
    return (
      <div
        className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-center text-gray-500"
        data-testid="server-player-stats-empty"
      >
        {t("playerStats.empty", "No player data available yet.")}
      </div>
    );
  }

  // ── Derived ──────────────────────────────────────────────────────────────
  const teamIds = Object.keys(data.team_totals);

  const filtered =
    teamFilter === "all"
      ? data.players
      : data.players.filter((p) => p.team_id === teamFilter);

  const sorted = [...filtered].sort((a, b) => {
    const diff = getPlayerValue(a, sortField) - getPlayerValue(b, sortField);
    return sortAsc ? diff : -diff;
  });

  const handleSort = (field: SortableNumericField) => {
    if (field === sortField) {
      setSortAsc((v) => !v);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  const handleExportCSV = async () => {
    if (!data) return;
    setExporting("csv");
    try {
      exportPlayerStatsCSV(data, { filename: `player-stats-${matchId}.csv` });
    } finally {
      setExporting(null);
    }
  };

  const handleExportPDF = async () => {
    if (!data) return;
    setExporting("pdf");
    try {
      exportPlayerStatsPDF(data, { filename: `player-stats-${matchId}.pdf` });
    } finally {
      setExporting(null);
    }
  };

  return (
    <section className="space-y-4" data-testid="server-player-stats-section">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("playerStats.title", "Player Statistics (Server)")}
        </h3>

        <div className="flex flex-wrap items-center gap-2">
          {/* Team filter */}
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
            <button
              onClick={() => setTeamFilter("all")}
              data-testid="server-ps-filter-all"
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                teamFilter === "all"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-gray-600 hover:bg-gray-200"
              }`}
            >
              {t("playerStats.allTeams", "All")}
            </button>
            {teamIds.map((tid) => (
              <button
                key={tid}
                onClick={() => setTeamFilter(tid)}
                data-testid={`server-ps-filter-${tid}`}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  teamFilter === tid
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-200"
                }`}
              >
                {tid}
              </button>
            ))}
          </div>

          {/* Export buttons */}
          <button
            onClick={handleExportCSV}
            disabled={exporting !== null}
            data-testid="server-ps-export-csv"
            className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {exporting === "csv"
              ? t("playerStats.exporting", "Exporting…")
              : t("playerStats.exportCSV", "Export CSV")}
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting !== null}
            data-testid="server-ps-export-pdf"
            className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {exporting === "pdf"
              ? t("playerStats.exporting", "Exporting…")
              : t("playerStats.exportPDF", "Export PDF")}
          </button>
        </div>
      </div>

      {/* Team totals summary */}
      {teamIds.length > 0 && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          data-testid="server-ps-team-totals"
        >
          {teamIds.map((tid) => {
            const totals: TeamTotals = data.team_totals[tid];
            return (
              <div
                key={tid}
                className="rounded-xl border border-gray-200 bg-white p-4 space-y-2"
              >
                <p className="text-sm font-semibold text-gray-700 truncate">
                  {t("playerStats.teamTotalsTitle", "Team")}: {tid}
                </p>
                <dl className="grid grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-600">
                  <div>
                    <dt className="text-gray-400 uppercase tracking-wide">
                      {t("playerStats.goals", "Goals")}
                    </dt>
                    <dd className="font-bold text-gray-900 text-lg">
                      {totals.goals}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-400 uppercase tracking-wide">
                      {t("playerStats.passAtt", "Pass Att.")}
                    </dt>
                    <dd className="font-medium text-gray-900">
                      {totals.passes.attempted}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-400 uppercase tracking-wide">
                      {t("playerStats.passComp", "Pass Comp.")}
                    </dt>
                    <dd className="font-medium text-gray-900">
                      {totals.passes.completed} (
                      {totals.passes.completion_pct.toFixed(1)}%)
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-400 uppercase tracking-wide">
                      {t("playerStats.shots", "Shots")}
                    </dt>
                    <dd className="font-medium text-gray-900">
                      {totals.shots.total}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-400 uppercase tracking-wide">
                      {t("playerStats.shotsOnTarget", "On Target")}
                    </dt>
                    <dd className="font-medium text-gray-900">
                      {totals.shots.on_target}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-400 uppercase tracking-wide">
                      {t("playerStats.cards", "Cards")}
                    </dt>
                    <dd className="font-medium text-gray-900">
                      <span className="text-yellow-600">
                        {totals.cards.yellow}Y
                      </span>{" "}
                      <span className="text-red-600">{totals.cards.red}R</span>
                    </dd>
                  </div>
                </dl>
              </div>
            );
          })}
        </div>
      )}

      {/* Per-player table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className="w-full text-sm min-w-[700px]"
            data-testid="server-ps-table"
          >
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-3 font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10 min-w-[150px]">
                  {t("playerStats.player", "Player")}
                </th>
                <th className="py-3 px-2 text-center text-xs text-gray-500 font-medium">
                  POS
                </th>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    title={col.label}
                    onClick={() => handleSort(col.key)}
                    className="py-3 px-2 text-center text-xs text-gray-500 font-semibold cursor-pointer select-none hover:bg-gray-100 transition-colors"
                    data-testid={`server-ps-col-${col.abbr}`}
                  >
                    {col.abbr}
                    {sortField === col.key && (
                      <span className="ml-0.5 text-indigo-500">
                        {sortAsc ? "↑" : "↓"}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => {
                const isRatingExpanded = expandedRatingPlayerId === p.player_id;
                return (
                  <Fragment key={p.player_id}>
                    <tr
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      data-testid={`server-ps-row-${p.player_id}`}
                    >
                      <td className="py-2 px-3 sticky left-0 bg-white z-10">
                        <div className="flex items-center gap-2">
                          {p.jersey_number !== null && (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white bg-indigo-500">
                              {p.jersey_number}
                            </span>
                          )}
                          <span
                            className="font-medium text-gray-800 truncate max-w-[110px]"
                            title={formatDisplayName(p.player_name)}
                          >
                            {formatDisplayName(p.player_name)}
                          </span>
                          {p.is_starter && (
                            <span className="text-xs text-emerald-600 font-medium">
                              ★
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2 text-center text-xs text-gray-500">
                        {p.position ?? "–"}
                      </td>
                      {COLUMNS.map((col) => {
                        if (col.key === "rating") {
                          const rating = p.rating ?? null;
                          const breakdown = p.rating_breakdown ?? null;
                          if (rating === null) {
                            return (
                              <td
                                key={col.key}
                                data-testid={`server-ps-cell-${p.player_id}-rating`}
                                className="py-2 px-2 text-center text-xs text-gray-300"
                                title={t(
                                  "playerStats.ratingUnavailableHint",
                                  "Rating not available for this player yet.",
                                )}
                              >
                                –
                              </td>
                            );
                          }
                          return (
                            <td
                              key={col.key}
                              data-testid={`server-ps-cell-${p.player_id}-rating`}
                              className="py-2 px-2 text-center text-xs"
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedRatingPlayerId(
                                    isRatingExpanded ? null : p.player_id,
                                  )
                                }
                                aria-expanded={isRatingExpanded}
                                disabled={!breakdown}
                                data-testid={`server-ps-rating-toggle-${p.player_id}`}
                                title={t(
                                  "playerStats.ratingBreakdownToggle",
                                  "View rating breakdown",
                                )}
                                className="inline-flex items-center gap-1 font-semibold text-gray-800 hover:text-indigo-600 disabled:cursor-default disabled:hover:text-gray-800"
                              >
                                {rating.toFixed(1)}
                                {p.rating_provisional && (
                                  <span
                                    className="text-[9px] font-bold text-amber-600"
                                    data-testid={`server-ps-rating-provisional-${p.player_id}`}
                                    title={t(
                                      "playerStats.ratingProvisionalHint",
                                      "Provisional — fewer than 15 minutes played.",
                                    )}
                                  >
                                    P
                                  </span>
                                )}
                              </button>
                            </td>
                          );
                        }

                        const val = getPlayerValue(p, col.key);
                        const display =
                          col.key === "passes.completion_pct"
                            ? val.toFixed(1) + "%"
                            : col.key === "xg"
                              ? val.toFixed(2)
                              : val;
                        const isAdvanced =
                          col.key === "xg" ||
                          col.key === "progressive_passes" ||
                          col.key === "progressive_carries";
                        return (
                          <td
                            key={col.key}
                            data-testid={
                              isAdvanced
                                ? `server-ps-cell-${p.player_id}-${col.key}`
                                : undefined
                            }
                            className={`py-2 px-2 text-center tabular-nums text-xs ${
                              val === 0
                                ? "text-gray-300"
                                : "text-gray-800 font-medium"
                            }`}
                          >
                            {val === 0 ? "–" : display}
                          </td>
                        );
                      })}
                    </tr>
                    {isRatingExpanded && p.rating_breakdown && (
                      <tr
                        className="bg-indigo-50/40 border-b border-gray-100"
                        data-testid={`server-ps-rating-breakdown-row-${p.player_id}`}
                      >
                        <td colSpan={COLUMNS.length + 2} className="py-3 px-4">
                          <div
                            className="space-y-2"
                            data-testid={`server-ps-rating-breakdown-${p.player_id}`}
                          >
                            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-gray-600">
                              <div>
                                <dt className="uppercase tracking-wide text-[10px] text-gray-400">
                                  {t("playerStats.ratingBaseline", "Baseline")}
                                </dt>
                                <dd className="font-semibold text-gray-900">
                                  {p.rating_breakdown.baseline.toFixed(1)}
                                </dd>
                              </div>
                              <div>
                                <dt className="uppercase tracking-wide text-[10px] text-gray-400">
                                  {t(
                                    "playerStats.ratingAdjustment",
                                    "Adjustment",
                                  )}
                                </dt>
                                <dd className="font-semibold text-gray-900">
                                  {p.rating_breakdown.total_adjustment >= 0
                                    ? "+"
                                    : ""}
                                  {p.rating_breakdown.total_adjustment.toFixed(
                                    2,
                                  )}
                                </dd>
                              </div>
                              <div>
                                <dt className="uppercase tracking-wide text-[10px] text-gray-400">
                                  {t(
                                    "playerStats.ratingPositionGroup",
                                    "Position Group",
                                  )}
                                </dt>
                                <dd className="font-semibold text-gray-900">
                                  {t(
                                    POSITION_GROUP_LABEL_KEYS[
                                      p.rating_breakdown.position_group
                                    ] ?? "playerStats.positionGroupOther",
                                    p.rating_breakdown.position_group,
                                  )}
                                </dd>
                              </div>
                              <div>
                                <dt
                                  className="uppercase tracking-wide text-[10px] text-gray-400 cursor-help"
                                  title={t(
                                    "playerStats.ratingGateMinutesHint",
                                    "Minutes used to compute this rating — differs from Minutes when a red card ends the player's involvement early (Minutes still counts the whole match).",
                                  )}
                                >
                                  {t(
                                    "playerStats.ratingGateMinutes",
                                    "Rated Minutes",
                                  )}
                                </dt>
                                <dd className="font-semibold text-gray-900">
                                  {p.rating_breakdown.gate_minutes.toFixed(0)}
                                  &apos;
                                </dd>
                              </div>
                            </dl>
                            {p.rating_breakdown.clamped && (
                              <p
                                className="text-amber-600 text-xs"
                                data-testid={`server-ps-rating-breakdown-${p.player_id}-clamped`}
                              >
                                {t(
                                  "playerStats.ratingClampedNote",
                                  "Rating adjusted to fit the 0–10 scale.",
                                )}
                              </p>
                            )}
                            {p.rating_breakdown.contributions.length > 0 ? (
                              <ul className="divide-y divide-gray-200">
                                {p.rating_breakdown.contributions.map((c) => (
                                  <li
                                    key={c.key}
                                    className="flex items-center justify-between py-1 text-xs text-gray-700"
                                    data-testid={`server-ps-rating-breakdown-${p.player_id}-contribution-${c.key}`}
                                  >
                                    <span>
                                      {contributionKeyLabel(t, c.key)} ×{" "}
                                      {c.count}
                                    </span>
                                    <span
                                      className={
                                        c.points >= 0
                                          ? "text-emerald-600 font-medium"
                                          : "text-rose-600 font-medium"
                                      }
                                    >
                                      {c.points >= 0 ? "+" : ""}
                                      {c.points.toFixed(2)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-gray-400 text-xs">
                                {t(
                                  "playerStats.ratingNoContributions",
                                  "No scoring events recorded yet.",
                                )}
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-400">
        {t("playerStats.generatedAt", "Generated at")}:{" "}
        {new Date(data.generated_at).toLocaleString()}
      </p>
    </section>
  );
}
